import { useEffect, useState } from "react";
import { Dialog, DialogContent } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import {
  Plus,
  Edit,
  Trash2,
  MapPin,
  Loader2,
  RefreshCw,
  Check,
  Lock,
} from "lucide-react";
import { Address } from "../../types/supplier";
import { toast } from "react-toastify";
import apiService from "../../services/apiService";
import { getLocalStorageItem } from "../../utils/Validation";
import { STATE_NAME_MAP } from "../../lib/gst-rules";

interface AddressManagementDialogProps {
  open: boolean;
  add: boolean;
  onOpenChange: (open: boolean) => void;
  addresses: Address[];
  onUpdate: (addresses: Address[]) => void;
  indianStates: any[];
  supplierId: string;
  initialEditAddress?: Address | null;
}

interface ApiResponse<T> {
  data: T;
  message?: string;
  status?: number;
}

const emptyForm = {
  address_type: "",
  address_line1: "",
  address_line2: "",
  city: "",
  state: "",
  pincode: "",
  gstin: "",
};

const inputCls =
  "w-full h-9 pl-3 pr-3 rounded-[8px] border border-[#dde3e4] bg-white text-[13px] font-medium text-[#151b1d] placeholder:text-[#7c8b91] focus:border-[#3f8b98] focus:ring-1 focus:ring-[#3f8b98] outline-none transition-all";
const labelCls = "block text-[11px] font-semibold uppercase tracking-wider text-[#526066] mb-1";

export function AddressManagementDialog({
  open,
  add,
  onOpenChange,
  addresses,
  onUpdate,
  indianStates,
  supplierId,
  initialEditAddress = null,
}: AddressManagementDialogProps) {
  const [editingAddress, setEditingAddress] = useState<Address | null>(null);
  const [isAddingNew, setIsAddingNew] = useState(add);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({ ...emptyForm });
  const [pincodeError, setPincodeError] = useState<string>("");

  const [isFetchingGst, setIsFetchingGst] = useState(false);
  const [gstVerified, setGstVerified] = useState(false);
  const [companyName, setCompanyName] = useState("");

  /* ─── GSTIN fetch — only via Fetch button click (never on paste/type) ─── */
  const applyGstinValue = (value: string) => {
    const upper = value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 15);
    setFormData((prev) => ({ ...prev, gstin: upper }));
    setGstVerified(false);
    setCompanyName("");
  };

  const handleFetchGstinClick = async () => {
    const clean = formData.gstin.trim().toUpperCase();
    if (clean.length !== 15) {
      toast.error("Please enter a valid 15-character GSTIN");
      return;
    }
    setIsFetchingGst(true);
    try {
      const response: any = await apiService.post(`/supplier/fetch-supplier-gst`, {
        gstin: clean,
      });
      const supplier = response?.supplier;
      if (!supplier) {
        setCompanyName("");
        setGstVerified(false);
        toast.error("No supplier details found for this GSTIN");
        return;
      }

      // State from GSTIN digits (fallback if API state is missing)
      const stateCode = clean.substring(0, 2);
      const extractedStateName = STATE_NAME_MAP[stateCode] || "";
      let matchedState = indianStates.find(
        (s: any) => s.state_name?.toLowerCase() === extractedStateName.toLowerCase()
      );
      if (!matchedState && extractedStateName) {
        const needle = extractedStateName.toLowerCase().replace(/[^a-z]/g, "");
        matchedState = indianStates.find((s: any) => {
          const hay = (s.state_name || "").toLowerCase().replace(/[^a-z]/g, "");
          return hay.includes(needle) || needle.includes(hay);
        });
      }

      const addr = supplier.address || {};
      const apiState = addr.state != null ? String(addr.state) : "";
      const stateFromApi = indianStates.find(
        (s: any) =>
          String(s.id) === apiState ||
          s.state_name?.toLowerCase() === apiState.toLowerCase()
      );

      setFormData((prev) => ({
        ...prev,
        gstin: supplier.gstin || clean,
        address_type:
          addr.address_type === "registered_address" || !addr.address_type
            ? prev.address_type || "Registered Office"
            : addr.address_type,
        address_line1: addr.address_line1 || prev.address_line1,
        address_line2: addr.address_line2 || prev.address_line2,
        city: addr.city || prev.city,
        state: stateFromApi
          ? String(stateFromApi.id)
          : matchedState
            ? String(matchedState.id)
            : prev.state,
        pincode: addr.pincode ? String(addr.pincode) : prev.pincode,
      }));
      setCompanyName(supplier.companyName || supplier.company_name || "");
      setGstVerified(true);
      toast.success("GSTIN verified · details fetched");
    } catch {
      setCompanyName("");
      setGstVerified(false);
      toast.error("Failed to fetch GSTIN details");
    } finally {
      setIsFetchingGst(false);
    }
  };

  /* ─── Lifecycle ────────────────────────────────────────────────── */
  const handleCancel = () => {
    if (add || initialEditAddress) { onOpenChange(false); return; }
    setIsAddingNew(false);
    setEditingAddress(null);
    setFormData({ ...emptyForm });
    setPincodeError("");
    setGstVerified(false);
    setCompanyName("");
  };

  const startAddNew = () => {
    setEditingAddress(null);
    setFormData({ ...emptyForm });
    setIsAddingNew(true);
    setPincodeError("");
    setGstVerified(false);
    setCompanyName("");
  };

  const handleEdit = (address: Address) => {
    setEditingAddress(address);
    setFormData({
      address_type: address.address_type || "",
      address_line1: address.address_line1 || "",
      address_line2: address.address_line2 || "",
      city: address.city || "",
      state: address.state != null ? String(address.state) : "",
      pincode: address.pincode || "",
      gstin: address.gstin || "",
    });
    setIsAddingNew(true);
    setPincodeError("");
    setGstVerified(!!address.gstin);
    setCompanyName("");
  };

  useEffect(() => {
    if (!open) {
      setIsAddingNew(false);
      setEditingAddress(null);
      setFormData({ ...emptyForm });
      setPincodeError("");
      setGstVerified(false);
      setCompanyName("");
      return;
    }
    if (initialEditAddress) { handleEdit(initialEditAddress); return; }
    if (add) { startAddNew(); return; }
    setIsAddingNew(false);
  }, [open, add, initialEditAddress?.id]);

  /* ─── Submit ───────────────────────────────────────────────────── */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.address_type || !formData.city || !formData.state || !formData.pincode || !formData.gstin) {
      toast.error("Please fill all required fields");
      return;
    }
    if (formData.pincode && !/^[1-9][0-9]{5}$/.test(formData.pincode)) {
      setPincodeError("Invalid pincode (e.g. 400001)");
      return;
    }

    const userId = getLocalStorageItem("detail")?.userId;
    setSaving(true);
    try {
      const response = await apiService.post<ApiResponse<{ address_id?: string | number }>>(
        `/supplier/add-update-supplier-address`,
        { ...formData, id: editingAddress?.id || undefined, supplierId, userId }
      );
      if (response.status === 200) {
        if (editingAddress) {
          onUpdate(addresses.map((a) => (a.id === editingAddress.id ? { ...editingAddress, ...formData } : a)));
          toast.success("Address updated successfully.");
        } else {
          const newAddr: Address = {
            id: String(response.data?.address_id ?? Date.now()),
            ...formData,
          };
          onUpdate([...addresses, newAddr]);
          toast.success("Address added successfully.");
        }
        setEditingAddress(null);
        setIsAddingNew(false);
        setFormData({ ...emptyForm });
        setPincodeError("");
        setGstVerified(false);
        setCompanyName("");
        if (add || initialEditAddress) onOpenChange(false);
      }
    } catch {
      toast.error("Failed to save address");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (addressId: string) => {
    try {
      const response = await apiService.post<ApiResponse<any>>(`/supplier/delete-supplier-address`, { id: addressId });
      if (response.status === 200) {
        onUpdate(addresses.filter((a) => a.id !== addressId));
        toast.success("Address removed.");
      }
    } catch {
      toast.error("Something went wrong, try again!");
    }
  };

  const handlePincodeChange = (value: string) => {
    const v = value.replace(/\D/g, "").slice(0, 6);
    setPincodeError(v && !/^[1-9][0-9]{5}$/.test(v) ? "Invalid pincode (e.g. 400001)" : "");
    setFormData((prev) => ({ ...prev, pincode: v }));
  };

  const showList = addresses.length > 0 && !add && !isAddingNew && !initialEditAddress;

  /* ─── Render ───────────────────────────────────────────────────── */
  return (
    <Dialog open={open || add} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[680px] w-[95vw] max-h-[92vh] overflow-y-auto rounded-[14px] border border-[#dde3e4] bg-white p-0 shadow-2xl font-['Poppins',sans-serif] text-[#151b1d]">

        {/* ── Header ── */}
        <div className="px-6 pt-5 pb-4 border-b border-[#eaeeef]">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-[10px] bg-[#e1f0f1] flex items-center justify-center flex-shrink-0">
              <MapPin style={{ width: 18, height: 18 }} className="text-[#3f8b98]" />
            </div>
            <div>
              <h2 className="text-[16px] font-semibold text-[#151b1d]">
                {isAddingNew
                  ? editingAddress
                    ? "Edit Address"
                    : "Add New Address"
                  : "Manage Addresses & GSTIN"}
              </h2>
              <p className="text-[12px] text-[#526066] mt-0.5">
                {isAddingNew
                  ? editingAddress
                    ? "Update address details and linked GSTIN"
                    : "Enter GSTIN and address details"
                  : "Manage registered and branch addresses with linked GSTIN"}
              </p>
            </div>
          </div>
        </div>

        <div className="px-6 py-5 space-y-5">

          {/* ── Address list ── */}
          {showList && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-bold uppercase tracking-widest text-[#7c8b91]">
                  Addresses ({addresses.length})
                </span>
                <button
                  onClick={startAddNew}
                  className="h-8 px-3 rounded-[7px] bg-[#3f8b98] hover:bg-[#2e6b76] text-white text-[12px] font-semibold flex items-center gap-1.5 transition-colors"
                >
                  <Plus className="w-3.5 h-3.5" />
                  Add Address
                </button>
              </div>
              {addresses.map((address) => (
                <div
                  key={address.id}
                  className="border border-[#dde3e4] rounded-[10px] bg-white overflow-hidden"
                >
                  <div className="flex items-center justify-between px-4 py-2.5 border-b border-[#eaeeef] bg-[#f6f8f8]">
                    <span className="text-[12px] font-semibold text-[#151b1d]">{address.address_type}</span>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => handleEdit(address)}
                        className="w-7 h-7 rounded-[6px] flex items-center justify-center text-[#526066] hover:bg-white hover:text-[#3f8b98] transition-colors"
                      >
                        <Edit className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => void handleDelete(address.id)}
                        className="w-7 h-7 rounded-[6px] flex items-center justify-center text-[#526066] hover:bg-[#fdf1f1] hover:text-[#b0453f] transition-colors"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                  <div className="px-4 py-3 space-y-1">
                    <p className="text-[13px] font-medium text-[#151b1d]">{address.address_line1}</p>
                    {address.address_line2 && (
                      <p className="text-[12px] text-[#526066]">{address.address_line2}</p>
                    )}
                    <p className="text-[12px] text-[#526066]">
                      {address.city},{" "}
                      {indianStates.find((s: any) => String(s.id) === String(address.state))?.state_name || address.state}
                      {" "}&mdash; {address.pincode}
                    </p>
                    {address.gstin && (
                      <div className="inline-flex items-center gap-1.5 mt-1.5 bg-[#e1f0f1] text-[#2e6b76] text-[11px] font-semibold px-2.5 py-1 rounded-[6px] font-mono">
                        GSTIN: {address.gstin}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* ── Empty state (no addresses, not adding) ── */}
          {!showList && !isAddingNew && (
            <div className="flex flex-col items-center justify-center py-8 gap-3">
              <div className="w-12 h-12 rounded-full bg-[#f6f8f8] flex items-center justify-center">
                <MapPin className="w-5 h-5 text-[#7c8b91]" />
              </div>
              <div className="text-center">
                <p className="text-[13px] font-semibold text-[#151b1d]">No addresses added yet</p>
                <p className="text-[12px] text-[#7c8b91] mt-0.5">Add a registered or branch address</p>
              </div>
              <button
                onClick={startAddNew}
                className="h-9 px-4 rounded-[8px] bg-[#3f8b98] hover:bg-[#2e6b76] text-white text-[13px] font-medium flex items-center gap-1.5 transition-colors"
              >
                <Plus className="w-4 h-4" />
                Add Address
              </button>
            </div>
          )}

          {/* ── Add / Edit form (same UI; empty on add, prefilled on edit) ── */}
          {isAddingNew && (
            <form onSubmit={handleSubmit} className="space-y-4">

              {/* ── Section 1: GSTIN ── */}
              <div className="border border-[#dde3e4] rounded-[10px] p-4 bg-white space-y-3">
                <div className="flex items-center gap-2">
                  <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold ${gstVerified ? "bg-[#e2f2e8] text-[#227a4c]" : "bg-[#3f8b98] text-white"}`}>
                    {gstVerified ? <Check className="w-3 h-3 stroke-[2.5]" /> : "1"}
                  </span>
                  <span className="text-[13px] font-semibold text-[#151b1d]">Verify GSTIN</span>
                </div>

                <div>
                  <label className={labelCls}>
                    GSTIN <span className="text-[#b0453f]">*</span>
                  </label>
                  <div className="flex items-center gap-2">
                    <div className="relative flex-1">
                      <input
                        type="text"
                        maxLength={15}
                        autoComplete="off"
                        placeholder="e.g. 29ABBCA4070B1Z3"
                        value={formData.gstin}
                        onChange={(e) => applyGstinValue(e.target.value)}
                        onPaste={(e) => {
                          e.preventDefault();
                          const pasted = e.clipboardData.getData("text") || "";
                          applyGstinValue(pasted);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") e.preventDefault();
                        }}
                        disabled={isFetchingGst || !!editingAddress}
                        readOnly={!!editingAddress}
                        className={`w-full h-9 pl-3.5 pr-9 rounded-[7px] border border-[#dde3e4] text-[13px] font-medium text-[#151b1d] focus:border-[#3f8b98] focus:ring-1 focus:ring-[#3f8b98] outline-none uppercase transition-all ${
                          editingAddress || isFetchingGst
                            ? "bg-[#f6f8f8] cursor-not-allowed text-[#526066]"
                            : "bg-white"
                        }`}
                      />
                      {isFetchingGst ? (
                        <div className="absolute right-2.5 top-1/2 -translate-y-1/2">
                          <Loader2 className="w-4 h-4 text-[#3f8b98] animate-spin" />
                        </div>
                      ) : editingAddress ? (
                        <div className="absolute right-2.5 top-1/2 -translate-y-1/2" title="GSTIN cannot be changed while editing">
                          <Lock className="w-3.5 h-3.5 text-[#7c8b91]" />
                        </div>
                      ) : gstVerified ? (
                        <div className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 rounded-full bg-[#e2f2e8] text-[#227a4c] flex items-center justify-center">
                          <Check className="w-3 h-3 stroke-[2.5]" />
                        </div>
                      ) : null}
                    </div>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        void handleFetchGstinClick();
                      }}
                      disabled={isFetchingGst || !!editingAddress}
                      className="h-9 px-3.5 rounded-[7px] border border-[#dde3e4] bg-white text-[#151b1d] hover:border-[#3f8b98] text-xs font-medium inline-flex items-center gap-1.5 transition-colors cursor-pointer shadow-sm flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <RefreshCw className={`w-3.5 h-3.5 text-[#526066] ${isFetchingGst ? "animate-spin text-[#3f8b98]" : ""}`} />
                      <span>{isFetchingGst ? "Fetching…" : "Fetch"}</span>
                    </button>
                  </div>
                  {editingAddress && (
                    <p className="mt-1.5 text-[11px] text-[#7c8b91]">
                      GSTIN is locked for existing addresses and cannot be edited.
                    </p>
                  )}

                  {companyName ? (
                    <p className="mt-2 text-[13px] font-medium text-[#151b1d]">{companyName}</p>
                  ) : null}

                  {isFetchingGst && (
                    <div className="mt-2 bg-[#e1f0f1] border border-[#b3d9de] text-[#2e6b76] rounded-[7px] px-3 py-1.5 text-xs font-medium flex items-center gap-1.5">
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      <span>Fetching GSTIN details from GST portal…</span>
                    </div>
                  )}

                  {gstVerified && !isFetchingGst && (
                    <div className="mt-2 bg-[#e2f2e8] border border-[#bfe9cf] text-[#227a4c] rounded-[7px] px-3 py-1.5 text-xs font-medium flex items-center gap-1.5">
                      <Check className="w-3.5 h-3.5" />
                      <span>
                        {editingAddress
                          ? "GSTIN linked · you can edit details below"
                          : "GSTIN verified · address details auto-fetched"}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* ── Section 2: Address Details ── */}
              <div className="border border-[#dde3e4] rounded-[10px] p-4 bg-white space-y-3">
                <div className="flex items-center gap-2">
                  <span className="w-5 h-5 rounded-full bg-[#3f8b98] text-white flex items-center justify-center text-xs font-bold">
                    2
                  </span>
                  <span className="text-[13px] font-semibold text-[#151b1d]">Address details</span>
                </div>

                {/* Address Type */}
                <div>
                  <label className={labelCls}>
                    Address Type <span className="text-[#b0453f]">*</span>
                  </label>
                  <Select
                    value={formData.address_type}
                    onValueChange={(v) => setFormData((prev) => ({ ...prev, address_type: v }))}
                  >
                    <SelectTrigger className="h-9 rounded-[8px] border-[#dde3e4] text-[13px] font-medium text-[#151b1d] focus:ring-1 focus:ring-[#3f8b98]">
                      <SelectValue placeholder="Select address type" />
                    </SelectTrigger>
                    <SelectContent className="bg-white z-[200]">
                      <SelectItem value="Registered Office">Registered Office</SelectItem>
                      <SelectItem value="Branch">Branch Office</SelectItem>
                      <SelectItem value="Warehouse">Warehouse</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Address Line 1 */}
                <div>
                  <label className={labelCls}>
                    Address Line 1 <span className="text-[#b0453f]">*</span>
                  </label>
                  <input
                    type="text"
                    placeholder="Street, building, locality"
                    value={formData.address_line1}
                    onChange={(e) => setFormData((prev) => ({ ...prev, address_line1: e.target.value }))}
                    className={inputCls}
                    required
                  />
                </div>

                {/* Address Line 2 */}
                <div>
                  <label className={labelCls}>Address Line 2</label>
                  <input
                    type="text"
                    placeholder="Landmark, area (optional)"
                    value={formData.address_line2}
                    onChange={(e) => setFormData((prev) => ({ ...prev, address_line2: e.target.value }))}
                    className={inputCls}
                  />
                </div>

                {/* City + State + Pincode */}
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className={labelCls}>
                      City <span className="text-[#b0453f]">*</span>
                    </label>
                    <input
                      type="text"
                      placeholder="City"
                      value={formData.city}
                      onChange={(e) => setFormData((prev) => ({ ...prev, city: e.target.value }))}
                      className={inputCls}
                      required
                    />
                  </div>

                  <div>
                    <label className={labelCls}>
                      State <span className="text-[#b0453f]">*</span>
                    </label>
                    {indianStates.length > 0 ? (
                      <div className="relative">
                        {gstVerified && !editingAddress && (
                          <Lock className="absolute right-7 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#7c8b91] z-10" />
                        )}
                        <Select
                          value={formData.state?.toString() || ""}
                          onValueChange={(v) => setFormData((prev) => ({ ...prev, state: v }))}
                          disabled={gstVerified && !editingAddress}
                        >
                          <SelectTrigger className={`h-9 rounded-[8px] border-[#dde3e4] text-[13px] font-medium text-[#151b1d] focus:ring-1 focus:ring-[#3f8b98] ${gstVerified && !editingAddress ? "bg-[#f6f8f8]" : ""}`}>
                            <SelectValue placeholder="State" />
                          </SelectTrigger>
                          <SelectContent className="max-h-60 overflow-y-auto bg-white z-[200]">
                            {indianStates.map((s: any) => (
                              <SelectItem key={s.id} value={String(s.id)}>
                                {s.state_name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    ) : (
                      <input
                        type="text"
                        placeholder="State"
                        value={formData.state}
                        onChange={(e) => setFormData((prev) => ({ ...prev, state: e.target.value }))}
                        className={inputCls}
                      />
                    )}
                  </div>

                  <div>
                    <label className={labelCls}>
                      Pincode <span className="text-[#b0453f]">*</span>
                    </label>
                    <input
                      type="text"
                      maxLength={6}
                      placeholder="6-digit PIN"
                      value={formData.pincode}
                      onChange={(e) => handlePincodeChange(e.target.value)}
                      className={`${inputCls} ${pincodeError ? "border-[#b0453f]" : ""}`}
                      required
                    />
                    {pincodeError && (
                      <p className="text-[11px] text-[#b0453f] mt-1">{pincodeError}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* ── Footer ── */}
              <div className="flex items-center justify-end gap-2.5 pt-1 border-t border-[#eaeeef]">
                <button
                  type="button"
                  onClick={handleCancel}
                  disabled={saving}
                  className="h-9 px-4 rounded-[8px] border border-[#dde3e4] bg-white text-[#151b1d] hover:border-[#3f8b98] text-[13px] font-medium transition-colors cursor-pointer disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="h-9 px-5 rounded-[8px] bg-[#3f8b98] hover:bg-[#2e6b76] text-white text-[13px] font-semibold flex items-center gap-1.5 transition-colors cursor-pointer disabled:opacity-50"
                >
                  {saving ? (
                    <>
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      Saving…
                    </>
                  ) : editingAddress ? (
                    "✓ Update Address"
                  ) : (
                    "✓ Add Address"
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
